import { AudioRepository } from "app/js/src/domain/audio-repository";
import { MediaRepository } from "app/js/src/domain/media-repository";

export type MamAudioSelection =
  | { kind: "track"; mediaId: number }
  | { kind: "embedded" };

export interface ResolveMamAudioSelectionResult {
  selection: MamAudioSelection;
  /**
   * True when persisted id was missing/stale and healed to first track,
   * or cleared to embedded (caller should save).
   */
  shouldPersistSelection: boolean;
}

export interface MamAudioRuntimeFields {
  /** Persisted / runtime linked-track id; undefined = embedded. */
  audioMediaId: number | undefined;
  defaultAudioPreviewUrl: string | undefined;
  shouldPersistSelection: boolean;
}

/** Reads persisted MAM audio id from project JSON / in-memory segment shapes. */
export function readPersistedMamAudioMediaId(source: {
  audioMediaId?: number | null;
  audio_media_id?: number | null;
  defaultAudioMediaId?: number | null;
} | null | undefined): number | undefined {
  if (source == null) return undefined;
  const raw =
    source.audioMediaId ?? source.audio_media_id ?? source.defaultAudioMediaId;
  const id = Number(raw);
  if (!Number.isFinite(id) || id <= 0) return undefined;
  return id;
}

/**
 * Resolves MAM linked-audio selection from a persisted id + current track list.
 * Missing/stale id + tracks → first track (heal). No tracks → embedded.
 * Valid id kept even when URL refresh fails later (handled by runtime helper).
 */
export function resolveMamAudioSelection(
  persistedAudioMediaId: number | null | undefined,
  trackIds: Array<{ id: number }> | null | undefined,
): ResolveMamAudioSelectionResult {
  const tracks = (trackIds ?? []).filter((t) => t?.id != null && t.id > 0);
  const persisted = Number(persistedAudioMediaId);
  const hasPersisted =
    Number.isFinite(persisted) && persisted > 0 ? persisted : undefined;

  if (hasPersisted != null && tracks.some((t) => t.id === hasPersisted)) {
    return {
      selection: { kind: "track", mediaId: hasPersisted },
      shouldPersistSelection: false,
    };
  }

  if (tracks.length > 0) {
    return {
      selection: { kind: "track", mediaId: tracks[0].id },
      shouldPersistSelection: true,
    };
  }

  return {
    selection: { kind: "embedded" },
    shouldPersistSelection: hasPersisted != null,
  };
}

/**
 * Loads track list for a MAM media, applies heal/embedded matrix, then signs URL.
 * Valid persisted id with URL failure keeps the id (preview soft-fallback only).
 * Heal-to-first with URL failure falls back to embedded and asks to persist.
 */
export async function resolveMamAudioRuntimeFields(
  mamMediaId: number,
  persistedAudioMediaId: number | null | undefined,
  mediaRepository: MediaRepository,
  audioRepository: AudioRepository,
): Promise<MamAudioRuntimeFields> {
  if (mamMediaId == null || mamMediaId <= 0) {
    return {
      audioMediaId: undefined,
      defaultAudioPreviewUrl: undefined,
      shouldPersistSelection: persistedAudioMediaId != null && persistedAudioMediaId > 0,
    };
  }

  let trackIds: Array<{ id: number }> = [];
  try {
    const info = await mediaRepository.getMediaInfo(mamMediaId);
    trackIds = (info?.audioFiles ?? [])
      .filter((f) => f?.id != null && f.id > 0)
      .map((f) => ({ id: f.id }));
  } catch {
    return {
      audioMediaId: undefined,
      defaultAudioPreviewUrl: undefined,
      shouldPersistSelection:
        persistedAudioMediaId != null && Number(persistedAudioMediaId) > 0,
    };
  }

  const resolved = resolveMamAudioSelection(persistedAudioMediaId, trackIds);
  if (resolved.selection.kind === "embedded") {
    return {
      audioMediaId: undefined,
      defaultAudioPreviewUrl: undefined,
      shouldPersistSelection: resolved.shouldPersistSelection,
    };
  }

  const selectedId = resolved.selection.mediaId;
  try {
    const response = await audioRepository.getAudioURL(selectedId);
    const url =
      (response && (response.url ?? response.Url)) != null
        ? String(response.url ?? response.Url)
        : "";
    if (url.trim()) {
      return {
        audioMediaId: selectedId,
        defaultAudioPreviewUrl: url,
        shouldPersistSelection: resolved.shouldPersistSelection,
      };
    }
  } catch {
    // fall through
  }

  // URL unavailable: keep a previously valid id; heal attempts fall back to embedded.
  if (resolved.shouldPersistSelection) {
    return {
      audioMediaId: undefined,
      defaultAudioPreviewUrl: undefined,
      shouldPersistSelection: true,
    };
  }

  return {
    audioMediaId: selectedId,
    defaultAudioPreviewUrl: undefined,
    shouldPersistSelection: false,
  };
}
